import React from 'react';
import { useTranslation } from 'react-i18next';
import { UploadIcon } from '@hero/web-icons';
import { FileUploader } from '../../../app/File/FileUploader/FileUploader';
import { Label } from '../../../ui/Label';

/**
 * VAT invoice files come straight out of accounting systems, so
 * across the bilateral SaaS flow we only accept PDFs — JPG/PNG
 * defeat the financing lenders' audit trail (no embedded text,
 * hard to verify). Both the create flow (`EligibleLegsDrawer`)
 * and the post-create upload flow (`UploadInvoiceProofDrawer`)
 * lean on this single constant so the constraint can't drift.
 */
export const VAT_FILE_TYPES: Record<string, string[]> = {
  'application/pdf': [],
};

export interface VatFileFieldProps {
  /** Stable id for the underlying label/input. */
  id?: string;
  /** Currently-selected files (single-file flow). */
  files: File[];
  /** Selection change handler. */
  onChange: (files: File[]) => void;
  /**
   * Render a `*` indicator next to the label and surface
   * "required" copy in the description. The actual validation
   * still happens in the parent (we don't enforce a submit guard
   * here so the field stays composable).
   */
  required?: boolean;
  /**
   * Optional override for the description shown inside the
   * dropzone. Defaults to the standard "Upload the PDF VAT invoice
   * from your accounting system" / "Required …" copy.
   */
  description?: string;
  /**
   * Optional override for the helper text rendered under the
   * dropzone. Useful for callers that want to swap to a
   * "required because …" style message.
   */
  helperText?: string;
}

/**
 * Shared VAT-invoice PDF uploader used by every place in the
 * dashboard where the seller attaches a VAT PDF — the
 * `EligibleLegsDrawer` create flow and the standalone
 * `UploadInvoiceProofDrawer`. Both surfaces render identical
 * dropzone, hint, status messaging, and accept list so the
 * constraint and the UX stay in lock-step.
 *
 * The component is intentionally PDF-only (see
 * {@link VAT_FILE_TYPES}). Wrap in your own field if you need a
 * broader accept list (e.g. admin tooling).
 */
export function VatFileField({
  id,
  files,
  onChange,
  required,
  description,
  helperText,
}: VatFileFieldProps) {
  const { t } = useTranslation(['m-invoice']);
  const hasFile = files.length > 0;
  const isPdf = !hasFile || files[0]?.type === 'application/pdf';

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>
        {t('m-invoice:vat_field.label', {
          defaultValue: 'VAT invoice file',
        })}
        {required && <span className="text-destructive ms-1">*</span>}
      </Label>
      <FileUploader
        files={hasFile ? files : undefined}
        onChange={onChange}
        acceptedFileTypes={VAT_FILE_TYPES}
        label={t('m-invoice:vat_field.label', {
          defaultValue: 'VAT invoice file',
        })}
        variant="default"
        description={
          description ??
          (required
            ? t('m-invoice:vat_field.description_required', {
                defaultValue:
                  'Required. Upload the PDF VAT invoice from your accounting system.',
              })
            : t('m-invoice:vat_field.description', {
                defaultValue:
                  'Optional. Upload the PDF VAT invoice from your accounting system now, or add it later from the invoice.',
              }))
        }
        hint={t('m-invoice:vat_field.hint', { defaultValue: 'PDF only' })}
        status={hasFile && !isPdf ? 'error' : hasFile ? 'success' : 'neutral'}
        statusMessage={
          hasFile && !isPdf
            ? t('m-invoice:vat_field.pdf_only_error', {
                defaultValue: 'VAT invoice must be a PDF.',
              })
            : hasFile
            ? t('m-invoice:vat_field.file_selected', {
                defaultValue: 'File selected',
              })
            : undefined
        }
        emptyIcon={<UploadIcon className="size-5 text-primary" />}
      />
      {helperText ? (
        <p className="text-xs text-muted-foreground">{helperText}</p>
      ) : null}
    </div>
  );
}
